import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Bell, AtSign, UserPlus } from "lucide-react";

export default function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/mark-read");
    load();
  };

  const openItem = async (n) => {
    if (!n.read) api.patch(`/notifications/${n.id}/read`).then(load);
    setOpen(false);
    if (n.project_id) navigate(`/projects/${n.project_id}`);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell-btn">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span data-testid="notification-unread-badge"
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 rounded-full bg-rose-500 text-white text-[9px] font-mono flex items-center justify-center">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-dropdown">
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Notifications</p>
          {data.unread > 0 && (
            <button className="text-xs text-primary hover:underline" data-testid="mark-all-read-btn" onClick={markAll}>
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto divide-y divide-border">
          {data.items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} data-testid="notification-item"
              className={`w-full text-left px-4 py-3 flex items-start gap-2.5 hover:bg-accent/60 transition-colors ${!n.read ? "bg-primary/5" : ""}`}>
              {n.type === "mention"
                ? <AtSign className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                : <UserPlus className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />}
              <div className="min-w-0">
                <p className={`text-sm leading-snug ${!n.read ? "font-medium" : "text-muted-foreground"}`}>{n.message}</p>
                <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                  {n.entity_ref && <span className="text-primary">{n.entity_ref} · </span>}
                  {new Date(n.created_at).toLocaleString()}
                </p>
              </div>
              {!n.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5 ml-auto" />}
            </button>
          ))}
          {!data.items.length && <p className="p-6 text-sm text-muted-foreground text-center">No notifications yet.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
